import Icon from '@/components/Icon'
import { useSiteData } from '@/context/SiteDataProvider'
import { faFacebookF, faInstagram, faLinkedinIn, faXTwitter } from '@fortawesome/free-brands-svg-icons'
import { faAngleDown, faChevronRight, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons'
import { faBars, faXmark } from '@fortawesome/sharp-light-svg-icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { BREAKPOINT_MEDIUM } from '../../utilities/breakpoints'
import Search from '../Search'
import styles from './NavigationMenu.module.scss'

// Grace period before a hovered menu closes, so overshooting the dropdown by a
// few pixels — or travelling diagonally toward it — doesn't dismiss it.
const HOVER_CLOSE_DELAY = 400

// The SCSS mobile rules use `max-width: $breakpoint-medium`, which still matches
// at exactly 1024px — so desktop starts one pixel above it. Anything narrower is
// laid out as the off-canvas mobile menu, where hover handlers must not run.
const DESKTOP_MEDIA_QUERY = `(min-width: ${BREAKPOINT_MEDIUM + 1}px)`

const isDesktopViewport = () => window.matchMedia(DESKTOP_MEDIA_QUERY).matches

export default function NavigationMenu() {
  const { headerMenuItems = [] } = useSiteData()
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
  const [openMenus, setOpenMenus] = useState({})
  const [isNavigating, setIsNavigating] = useState(false)
  const router = useRouter()
  const navRef = useRef(null)
  const blurTimeoutRef = useRef(null)
  const closeTimeoutRef = useRef(null)

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      if (navRef.current && !navRef.current.contains(document.activeElement)) {
        setOpenMenus({})
      }
    }, 100)
  }

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
    }
  }

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  useEffect(
    () => () => {
      clearTimeout(blurTimeoutRef.current)
      clearTimeout(closeTimeoutRef.current)
    },
    []
  )

  useEffect(() => {
    if (mobileMenuVisible) {
      document.body.classList.add('mobile-menu-open')
    } else {
      document.body.classList.remove('mobile-menu-open')
    }

    return () => document.body.classList.remove('mobile-menu-open')
  }, [mobileMenuVisible])

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY)
    const handleViewportChange = (e) => {
      cancelScheduledClose()
      setOpenMenus({})
      if (e.matches) {
        setMobileMenuVisible(false)
      }
    }
    mql.addEventListener('change', handleViewportChange)
    return () => mql.removeEventListener('change', handleViewportChange)
  }, [cancelScheduledClose])

  useEffect(() => {
    const handleRouteChangeStart = () => {
      cancelScheduledClose()
      setIsNavigating(true)
      setMobileMenuVisible(false)
      setOpenMenus({})
    }

    const handleRouteChangeComplete = () => {
      setIsNavigating(false)
    }

    const handleRouteChangeError = () => {
      setIsNavigating(false)
    }

    // Listen for all router events that indicate URL changes
    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', handleRouteChangeError)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', handleRouteChangeError)
    }
  }, [router.events, cancelScheduledClose])

  // Additional effect to handle URL parameter changes
  useEffect(() => {
    const handleUrlChange = () => {
      if (mobileMenuVisible) {
        setMobileMenuVisible(false)
        setOpenMenus({})
      }
    }

    // Listen for any URL changes including query parameters
    router.events.on('routeChangeComplete', handleUrlChange)
    router.events.on('hashChangeComplete', handleUrlChange)

    return () => {
      router.events.off('routeChangeComplete', handleUrlChange)
      router.events.off('hashChangeComplete', handleUrlChange)
    }
  }, [router.events, mobileMenuVisible])

  // Alternative approach: Listen to router.asPath changes
  const previousPath = useRef(router.asPath)
  useEffect(() => {
    if (previousPath.current !== router.asPath && mobileMenuVisible) {
      // Use setTimeout to defer state updates
      const timer = setTimeout(() => {
        setMobileMenuVisible(false)
        setOpenMenus({})
      }, 0)
      return () => clearTimeout(timer)
    }
    previousPath.current = router.asPath
  }, [router.asPath, mobileMenuVisible])

  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible)
    setOpenMenus({})
  }

  // Toggle menu open/closed state and handle parent-child relationships
  const toggleMenu = useCallback(
    (id, itemType = 'parent') => {
      setOpenMenus((prev) => {
        const newState = { ...prev }
        const isOpening = !prev[id]

        if (itemType === 'child') {
          // For child toggles, keep the parent open and close sibling children
          headerMenuItems.forEach((item) => {
            item.children?.forEach((child) => {
              if (child.children?.length) {
                newState[child.id] = child.id === id ? isOpening : false
              }
              child.children?.forEach((grandchild) => {
                newState[grandchild.id] = false
              })
            })
          })
        } else {
          // For parent toggles, close everything then open the target
          Object.keys(prev).forEach((key) => {
            newState[key] = false
          })
          if (isOpening) {
            newState[id] = true
          }
        }

        return newState
      })
    },
    [headerMenuItems]
  )

  const handleKeyDown = useCallback(
    (e, id, itemType = 'parent') => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggleMenu(id, itemType)
      } else if (e.key === 'Escape' && openMenus[id]) {
        toggleMenu(id, itemType)
      }
    },
    [toggleMenu, openMenus]
  )

  // Handle link click and set navigation state
  const handleLinkClick = () => {
    setIsNavigating(true)
    setMobileMenuVisible(false)
    setOpenMenus({})
  }

  const handleParentOpen = (id) => {
    if (!isDesktopViewport()) return
    // Re-entering cancels a pending close, so a brief overshoot is forgiven.
    cancelScheduledClose()
    setOpenMenus(() => {
      const newState = {}
      headerMenuItems.forEach((item) => {
        newState[item.id] = item.id === id
        item.children?.forEach((child) => {
          newState[child.id] = false
          child.children?.forEach((grandchild) => {
            newState[grandchild.id] = false
          })
        })
      })
      return newState
    })
  }

  // Opens a submenu item's flyout and closes its siblings'. Items without
  // children match nothing, which collapses whatever flyout was open. Driven by
  // hover and, on desktop, by the chevron click — see handleChevronClick.
  const openChildFlyout = (childId) => {
    if (!isDesktopViewport()) return
    cancelScheduledClose()
    setOpenMenus((prevState) => {
      const newState = { ...prevState }
      headerMenuItems.forEach((item) => {
        item.children?.forEach((child) => {
          if (child.children?.length) {
            newState[child.id] = child.id === childId
          }
          child.children?.forEach((grandchild) => {
            newState[grandchild.id] = false
          })
        })
      })
      return newState
    })
  }

  // A mouse click is always preceded by mouseenter, so on desktop the hover
  // handler has already opened this menu by the time the click lands. Toggling
  // from that state would read `true` and close it again, making the menu
  // unopenable by mouse. Clicking therefore only ensures the menu is open;
  // hover-out still closes it. Touch fires no hover, so tapping keeps toggle
  // semantics, and keyboard is unaffected because focus sets no hover state.
  const handleParentClick = (id) => {
    if (isDesktopViewport()) {
      handleParentOpen(id)
      return
    }
    toggleMenu(id, 'parent')
  }

  const handleChevronClick = (childId) => {
    if (isDesktopViewport()) {
      openChildFlyout(childId)
      return
    }
    toggleMenu(childId, 'child')
  }

  const normalizePath = (path) => path.replace(/\/+$/, '')

  // Helper function to generate submenu/subsubmenu classes including navigation state
  const getMenuClass = (menuId, baseStyle) => {
    const baseClasses = [baseStyle]
    if (isNavigating) {
      baseClasses.push(styles.navigating)
    } else if (openMenus[menuId]) {
      baseClasses.push(styles.open)
    } else {
      baseClasses.push(styles.closed)
    }
    return baseClasses.join(' ')
  }

  const handleParentMouseLeave = (id) => {
    if (!isDesktopViewport()) return
    cancelScheduledClose()
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null
      const menuRoot = document.getElementById(`submenu-${id}`)?.closest('li')
      if (menuRoot?.contains(document.activeElement)) return
      setOpenMenus((prevState) => {
        const newState = { ...prevState }
        newState[id] = false
        const parent = headerMenuItems.find((item) => item.id === id)
        parent?.children?.forEach((child) => {
          newState[child.id] = false
          child.children?.forEach((grandchild) => {
            newState[grandchild.id] = false
          })
        })

        return newState
      })
    }, HOVER_CLOSE_DELAY)
  }

  return (
    <>
      <button
        type="button"
        className={styles.navtoggle}
        aria-label="Toggle navigation"
        data-cy="nav-toggle"
        onClick={toggleMobileMenu}
        aria-expanded={mobileMenuVisible}
      >
        {!mobileMenuVisible ? <Icon icon={faBars} /> : <Icon icon={faXmark} />}
      </button>

      <nav className={`${styles.mainNav} ${mobileMenuVisible ? styles.show : ''}`} ref={navRef} onBlur={handleBlur} onFocus={handleFocus}>
        <ul>
          <li className={styles.searchMobileItem} data-cy="search-mobile">
            <Search alwaysOpen={true} className={styles.searchMobile}></Search>
          </li>
          {headerMenuItems.map((item) => (
            <li
              key={item.id}
              className={`${normalizePath(router.asPath) === normalizePath(item.path) ? styles.active : ''}`}
              onMouseEnter={() => handleParentOpen(item.id)}
              onMouseLeave={() => handleParentMouseLeave(item.id)}
            >
              <div
                className={styles.parentLink}
                {...(item.children?.length
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      'data-cy': 'nav-parent',
                      onClick: () => handleParentClick(item.id),
                      onKeyDown: (e) => handleKeyDown(e, item.id, 'parent'),
                      onFocus: () => handleParentOpen(item.id),
                      'aria-expanded': openMenus[item.id] || false,
                      'aria-controls': `submenu-${item.id}`
                    }
                  : {})}
              >
                {item.children?.length ? (
                  <span>{item.label}</span>
                ) : (
                  <Link data-cy="nav-top-link" onClick={handleLinkClick} href={item.path}>
                    {item.label}
                  </Link>
                )}
                {item.children?.length ? (
                  <span className={styles.arrow} aria-hidden="true">
                    <Icon icon={faAngleDown} />
                  </span>
                ) : null}
              </div>

              {item.children?.length > 0 && (
                <ul
                  id={`submenu-${item.id}`}
                  data-cy="nav-submenu"
                  className={getMenuClass(item.id, styles.submenu)}
                  aria-hidden={!openMenus[item.id] || isNavigating}
                >
                  {item.children.map((child) => (
                    <li
                      key={child.id}
                      className={`${normalizePath(router.asPath) === normalizePath(child.path) ? styles.active : ''} ${child.children?.length ? styles.hasSubSubmenu : ''}`}
                      onMouseEnter={() => openChildFlyout(child.id)}
                    >
                      {child.children?.length > 0 ? (
                        <div className={styles.submenuItem}>
                          <Link
                            className={styles.submenuLink}
                            data-cy="nav-child-link"
                            href={child.path}
                            onClick={handleLinkClick}
                            tabIndex={openMenus[item.id] ? 0 : -1}
                          >
                            {child.label}
                          </Link>

                          <button
                            type="button"
                            className={styles.chevron}
                            data-cy="nav-child-toggle"
                            aria-label={`Toggle ${child.label} submenu`}
                            aria-expanded={openMenus[child.id] || false}
                            aria-controls={`subsubmenu-${child.id}`}
                            tabIndex={openMenus[item.id] ? 0 : -1}
                            onClick={() => handleChevronClick(child.id)}
                            onKeyDown={(e) => handleKeyDown(e, child.id, 'child')}
                          >
                            <Icon icon={faChevronRight} />
                          </button>
                        </div>
                      ) : (
                        <Link
                          className={styles.submenuItem}
                          data-cy="nav-leaf-link"
                          onClick={handleLinkClick}
                          href={child.path}
                          tabIndex={openMenus[item.id] ? 0 : -1}
                        >
                          {child.label}
                        </Link>
                      )}

                      {child.children?.length > 0 && (
                        <ul
                          id={`subsubmenu-${child.id}`}
                          data-cy="nav-subsubmenu"
                          className={getMenuClass(child.id, styles.subSubmenu)}
                          aria-hidden={!openMenus[child.id] || isNavigating}
                        >
                          {child.children.map((grandchild) => (
                            <li
                              key={grandchild.id}
                              className={`${normalizePath(router.asPath) === normalizePath(grandchild.path) ? styles.active : ''}`}
                            >
                              <Link
                                onClick={handleLinkClick}
                                href={grandchild.path}
                                tabIndex={openMenus[child.id] ? 0 : -1}
                                aria-current={normalizePath(router.asPath) === normalizePath(grandchild.path) ? 'page' : undefined}
                              >
                                {grandchild.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li className={styles.searchDesktopItem} data-cy="search-desktop">
            <Search className={styles.searchDesktop}></Search>
          </li>
        </ul>

        {mobileMenuVisible && (
          <div className={styles.contactInfo}>
            <ul>
              <li>
                <Icon icon={faEnvelope} className={styles.icons} />
                <Link aria-label="Email" href="mailto:info@integrityxd.com" target="_blank" rel="noreferrer">
                  info@integrityxd.com
                </Link>
              </li>
              <li>
                <Icon icon={faPhone} className={styles.icons} />
                <Link aria-label="Telephone" href="tel:314.727.3600" target="_blank" rel="noreferrer">
                  314.727.3600
                </Link>
              </li>
            </ul>
            <Link aria-label="LinkedIn" href="https://www.linkedin.com/company/integrity/" target="_blank" rel="noreferrer">
              <Icon icon={faLinkedinIn} className={styles.icons} />
            </Link>
            <Link aria-label="FaceBook" href="https://www.facebook.com/integritySTL/" target="_blank" rel="noreferrer">
              <Icon icon={faFacebookF} className={styles.icons} />
            </Link>
            <Link aria-label="X" href="https://x.com/Integrity_XD" target="_blank" rel="noreferrer">
              <Icon icon={faXTwitter} className={styles.icons} />
            </Link>
            <Link aria-label="Instagram" href="https://www.instagram.com/integrity_xd/" target="_blank" rel="noreferrer">
              <Icon icon={faInstagram} className={styles.icons} />
            </Link>
          </div>
        )}
      </nav>
    </>
  )
}
